import { z } from 'zod';
import { STREAM_STATUSES } from './stream-state.ts';
import { REMOVAL_REASON_IDS } from './safety.ts';

export const StreamStatusSchema = z.enum(STREAM_STATUSES);

/**
 * Who can find a stream. Unlisted (the default): anyone with the link. Public: also listed on Live
 * Now while it is live; it can be switched either way mid-stream. Private: a finished stream's
 * replay that only its creator can watch.
 */
export const StreamVisibility = z.enum(['unlisted', 'public', 'private']);
export type StreamVisibility = z.infer<typeof StreamVisibility>;

export const MAX_TITLE_LENGTH = 120;

// Line breaks and tabs become spaces; other control characters (C0, DEL, C1) and the bidi
// embedding/override/isolate controls, which can make a title display as something else, go.
const WHITESPACE_CONTROLS = /[\t\n\v\f\r\u0085\u2028\u2029]/g;
const OTHER_CONTROLS = /[\p{Cc}\u202A-\u202E\u2066-\u2069]/gu;

/** A stream title as stored and shown: control characters stripped, runs of spaces collapsed, trimmed. */
export function cleanTitle(raw: string): string {
  return raw.replace(WHITESPACE_CONTROLS, ' ').replace(OTHER_CONTROLS, '').replace(/\s+/g, ' ').trim();
}

/** Title input: cleaned, then at most 120 characters. An empty title is no title (null). */
export const StreamTitle = z
  .string()
  .max(MAX_TITLE_LENGTH * 4)
  .transform(cleanTitle)
  .pipe(z.string().max(MAX_TITLE_LENGTH, `Titles can be at most ${MAX_TITLE_LENGTH} characters.`))
  .transform((t) => t || null);

export const CreateStreamRequest = z.object({
  /** Leave out to use the creator's last title; empty or null for none. */
  title: StreamTitle.nullable().optional(),
  source: z.enum(['browser', 'mobile', 'obs']).default('browser'),
  visibility: z.enum(['unlisted', 'public']).default('unlisted'),
  /** Tell the channel's followers when this broadcast goes live (default on). */
  notifyFollowers: z.boolean().optional(),
});
export type CreateStreamRequest = z.infer<typeof CreateStreamRequest>;

/**
 * What a stream shows, for browsing. Set by the periodic AI scan of live streams (the same clip
 * moderation looks at). Ids are stable and stored; labels are for display.
 */
export const STREAM_CATEGORIES = [
  { id: 'gaming', label: 'Gaming' },
  { id: 'music', label: 'Music' },
  { id: 'talk', label: 'Just chatting' },
  { id: 'sports', label: 'Sports' },
  { id: 'fitness', label: 'Fitness' },
  { id: 'food', label: 'Food & cooking' },
  { id: 'outdoors', label: 'IRL & outdoors' },
  { id: 'creative', label: 'Art & making' },
  { id: 'learning', label: 'Learning & tech' },
  { id: 'tabletop', label: 'Cards & board games' },
  { id: 'animals', label: 'Animals' },
  { id: 'other', label: 'Other' },
] as const;
export type StreamCategory = (typeof STREAM_CATEGORIES)[number]['id'];
export const STREAM_CATEGORY_IDS = STREAM_CATEGORIES.map((c) => c.id) as [StreamCategory, ...StreamCategory[]];

/** Public view of a stream. Safe to show to anyone holding the link. */
export const PublicStream = z.object({
  id: z.string(),
  title: z.string().nullable(),
  status: StreamStatusSchema,
  channel: z.object({
    displayName: z.string().nullable(),
    handle: z.string().nullable(),
    /** The creator's profile photo on the media CDN; null shows their initial. */
    photoUrl: z.string().nullable(),
    /** People following the channel (absent where the response doesn't load it). */
    followers: z.number().int().nonnegative().optional(),
  }),
  playbackUrl: z.string().nullable(),
  watchUrl: z.string(),
  createdAt: z.number(),
  startedAt: z.number().nullable(),
  endedAt: z.number().nullable(),
  /** Taken down for breaking the terms: no video or replay is served. */
  removed: z.boolean().optional(),
  /** Why it was taken down (with `removed`). */
  removalReason: z.enum(REMOVAL_REASON_IDS).optional(),
  /** What the stream shows, from the AI scan; null until a scan has run. */
  category: z.enum(STREAM_CATEGORY_IDS).nullable(),
  /** People watching now (0 unless live), most at once, and different people who opened the link. */
  viewers: z.number().int().nonnegative(),
  peakViewers: z.number().int().nonnegative(),
  views: z.number().int().nonnegative(),
  /** Replay only the creator can watch. Everyone else gets no title and no video. */
  private: z.boolean().optional(),
  /** Deleted by its creator: nothing about it is shown any more. */
  deleted: z.boolean().optional(),
  /** The replay was kept as long as its retention allows (24 h guests, 30 days signed in) and is gone. */
  replayExpired: z.boolean().optional(),
  /** Listed on Live Now while live (visibility 'public'). */
  listed: z.boolean(),
  /** Thumbs up from viewers, and floating reactions sent. Dislikes are only shown to the creator. */
  likes: z.number().int().nonnegative(),
  dislikes: z.number().int().nonnegative().optional(),
  reactions: z.number().int().nonnegative(),
  /**
   * Latest preview frame (16:9 JPEG, about 960x540), grabbed about once a minute while live. Null
   * until the first one, and for a private replay (except for its creator), a deleted stream or
   * one taken down.
   */
  thumbnailUrl: z.string().nullable(),
});
export type PublicStream = z.infer<typeof PublicStream>;

/** Creator console: who can watch a finished stream's replay. */
export const ReplayVisibility = z.enum(['unlisted', 'private']);
export type ReplayVisibility = z.infer<typeof ReplayVisibility>;
/**
 * Owner changes to a stream. The title can change at any time (empty or null clears it). Public or
 * unlisted at any time; private only once the stream has ended.
 */
export const UpdateStreamRequest = z
  .object({ visibility: StreamVisibility.optional(), title: StreamTitle.nullable().optional() })
  .refine((b) => b.visibility !== undefined || b.title !== undefined, { message: 'Nothing to change' });

/** Floating reactions viewers can send. Ids are stored and sent; emoji are for display. */
export const REACTIONS = [
  { id: 'heart', emoji: '❤️' },
  { id: 'fire', emoji: '🔥' },
  { id: 'laugh', emoji: '😂' },
  { id: 'wow', emoji: '😮' },
  { id: 'clap', emoji: '👏' },
  { id: 'party', emoji: '🎉' },
] as const;
export type ReactionId = (typeof REACTIONS)[number]['id'];
export const REACTION_IDS = REACTIONS.map((r) => r.id) as [ReactionId, ...ReactionId[]];
/** A viewer's taps since their last send (the page batches them, about once a second). */
export const SendReactionsRequest = z.object({ counts: z.partialRecord(z.enum(REACTION_IDS), z.number().int().min(1).max(20)) });
export const VoteRequest = z.object({ value: z.union([z.literal(1), z.literal(-1), z.literal(0)]) });

/** Owner-only view. Contains the publish credentials. */
export const OwnedStream = PublicStream.extend({
  publish: z.object({
    whipUrl: z.string(),
    rtmpUrl: z.string(),
    streamKey: z.string(),
    token: z.string(),
  }),
  maxDurationSeconds: z.number(),
  expiresAt: z.number(),
  /** The creator's "Notify followers" switch for this broadcast. */
  notifyFollowers: z.boolean().optional(),
});
export type OwnedStream = z.infer<typeof OwnedStream>;

export const StatusEvent = z.object({
  type: z.literal('status'),
  stream: PublicStream,
});
export type StatusEvent = z.infer<typeof StatusEvent>;

/** Pushed over the stream WebSocket when the audience numbers or the category change (checked every 5 s while live). */
export interface AudienceEvent {
  type: 'audience';
  viewers: number;
  peakViewers: number;
  views: number;
  category: StreamCategory | null;
  likes: number;
  reactions: number;
}

/** Reactions everyone sent in the last moment, pushed about twice a second while people react. */
export interface ReactionEvent {
  type: 'reactions';
  counts: Partial<Record<ReactionId, number>>;
}

/** Callbacks from the ingest agent to the API. Authenticated with a shared secret. */
export const IngestAuthorizeRequest = z.object({
  /** Per-stream key (browser/WHIP, one-off RTMP). */
  streamId: z.string().optional(),
  /** Persistent per-channel OBS key: the API picks or creates the stream. */
  obsKeyId: z.string().optional(),
  token: z.string(),
  protocol: z.string().optional(),
  ip: z.string().optional(),
  /** The ingest machine the publisher is connected to (agents before pools omit it). */
  machineId: z.string().optional(),
});

export const IngestEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('publisher_connected'), streamId: z.string(), machineId: z.string(), protocol: z.string().optional() }),
  z.object({ type: z.literal('first_segment'), streamId: z.string(), machineId: z.string(), playlistKey: z.string() }),
  z.object({ type: z.literal('publisher_disconnected'), streamId: z.string(), machineId: z.string() }),
  z.object({ type: z.literal('stream_finalized'), streamId: z.string(), machineId: z.string(), segments: z.number().int().nonnegative(), durationSeconds: z.number().nonnegative() }),
  z.object({ type: z.literal('upload_failed'), streamId: z.string(), machineId: z.string(), detail: z.string().max(500) }),
  /** OBS keyframe interval too long for 1 s segments (measured median segment length). */
  z.object({ type: z.literal('keyframe_interval_high'), streamId: z.string(), machineId: z.string(), seconds: z.number().positive() }),
  /** A new preview frame is in storage at `key` (live/<id>/thumbs/<random>.jpg). */
  z.object({ type: z.literal('thumbnail'), streamId: z.string(), machineId: z.string(), key: z.string().max(200) }),
]);
export type IngestEvent = z.infer<typeof IngestEvent>;

export const ApiError = z.object({
  error: z.object({ code: z.string(), message: z.string() }),
});
export type ApiError = z.infer<typeof ApiError>;

/** Live chat requests (see chat.ts). */
export const ChatSendRequest = z.object({
  text: z.string().max(2000),
  replyTo: z.number().int().positive().optional(),
  /** Client-chosen id: a retried send returns the first message instead of posting twice. */
  clientId: z.string().min(8).max(64),
});

export const ChatSettingsRequest = z.object({
  mode: z.enum(['everyone', 'verified', 'off']).optional(),
  slowSeconds: z.union([z.literal(0), z.literal(5), z.literal(30)]).optional(),
});
